import {IDrawPredictorStore} from "apps/DRAW_PREDICTOR/modules/types/store";
import {createSelector} from "reselect";
import {getSquads, getSquadsByPot} from "apps/DRAW_PREDICTOR/modules/selectors";
import {Confederation, Groups} from "apps/DRAW_PREDICTOR/modules/enums";
import {filter, find, size, reduce, findKey, keyBy} from "lodash";
import {IPrediction} from "apps/DRAW_PREDICTOR/modules/reducers";
import {LOCKED_PREDICTION} from "apps/DRAW_PREDICTOR/modules/constants";

export const getPredictionsState = (state: IDrawPredictorStore) => state.predictions;

export const getActivePot = createSelector(getPredictionsState, ({activePot}) => activePot);
export const getSelectedSquadId = createSelector(
	getPredictionsState,
	({selectedSquad}) => selectedSquad
);
export const getSquadForEditId = createSelector(
	getPredictionsState,
	({predictionId1ForEdit}) => predictionId1ForEdit
);
export const getSquad2ForEditId = createSelector(
	getPredictionsState,
	({predictionId2ForEdit}) => predictionId2ForEdit
);
export const getAllPredictions = createSelector(
	getPredictionsState,
	({predictions}) => predictions
);

export const getPredictionBySquadId = createSelector(
	getAllPredictions,
	(predictions) => (squadId: number) =>
		find(predictions, (prediction) => prediction.squadId === squadId) || null
);

export const getPredictionByGroup = createSelector(
	getAllPredictions,
	(predictions) => (group: Groups) =>
		find(predictions, (prediction) => prediction.namegroup === group) || null
);

export const getPredictionByPositionAndGroup = createSelector(
	getAllPredictions,
	(predictions) => (position: number, group: Groups) =>
		find(
			predictions,
			(prediction) => prediction.position === position && prediction.namegroup === group
		) || null
);

export const getSquadByPositionAndGroup = createSelector(
	getSquads,
	getPredictionByPositionAndGroup,
	(squads, getSelectedPrediction) => (position: number, group: Groups) => {
		const selectedPrediction = getSelectedPrediction(position, group);

		if (selectedPrediction) {
			return find(squads, (squad) => squad.id === selectedPrediction.squadId) || null;
		}

		return null;
	}
);

export const getPredictionAndGroupBySquadId = createSelector(
	getAllPredictions,
	getSquads,
	(predictions, squads) => (squadId: number | null) => ({
		prediction: predictions.find((prediction) => prediction.squadId === squadId) || null,
		squad: squads.find((prediction) => prediction.id === squadId) || null,
	})
);

export const getSquadsById = createSelector(getSquads, (squads) => keyBy(squads, "id"));

export const checkPredictionByConfederationAndGroup = createSelector(
	getAllPredictions,
	getSquads,
	(allPredictions, squads) => (confederation: Confederation, group: string) => {
		/*
		 * 2 teams from the same confederation cannot be drawn into the same group.  Confederations where this applies are:
		 * AFC
		 * CAF
		 * CONCACF
		 * CONEMBOL
		 * OFC
		 * A maximum of 2 UEFA teams can be drawn into the same group.
		 * */
		const countTeamAllowedInCurrentConfederation = confederation === Confederation.UEFA ? 2 : 1;

		// Get all other predictions in current group
		const predictionsInCurrentGroup = filter(
			allPredictions,
			(prediction) => prediction.namegroup === group
		);

		// Converting predictions to squads
		const squadsInCurrentGroup = predictionsInCurrentGroup.map((prediction) =>
			find(squads, (squad) => squad.id === prediction.squadId)
		);

		const squadsInCurrentConfederation = filter(
			squadsInCurrentGroup,
			(squad) => squad?.confederation === confederation
		);

		return size(squadsInCurrentConfederation) !== countTeamAllowedInCurrentConfederation;
	}
);

export const getPredictionsByGroups = createSelector(getAllPredictions, (predictions) =>
	reduce(
		Groups,
		(result, value) => ({
			...result,
			[value]: filter(predictions, (prediction) => prediction.namegroup === value),
		}),
		{}
	)
);

export const getAvailableGroupForPot1 = createSelector(
	getPredictionsByGroups,
	(predictionsByGroups) =>
		findKey(predictionsByGroups, (predictions) => size(predictions) !== 1) || "a"
);

export const getAvailableGroupForActivePot = createSelector(
	getActivePot,
	getPredictionsByGroups,
	getSquads,
	getSelectedSquadId,
	checkPredictionByConfederationAndGroup,
	getSquadsByPot,
	(activePot, predictionsByGroups, squads, selectedSquadId, checkPrediction, squadsByPots) => {
		// Get squad by squad id
		const selectedSquad = find(squads, (squad) => squad.id === selectedSquadId) || null;

		const findOtherSquadFromCurrentPot = squadsByPots(activePot);

		return (
			findKey(
				predictionsByGroups,
				(predictions, group) =>
					selectedSquad &&
					checkPrediction(selectedSquad.confederation, group) &&
					!findOtherSquadFromCurrentPot.find((squad) =>
						find(
							predictions,
							(prediction: IPrediction) => squad.id === prediction.squadId
						)
					)
			) || null
		);
	}
);

export const checkIsSelectedSquadByPot = createSelector(
	getActivePot,
	getAvailableGroupForPot1,
	getAvailableGroupForActivePot,
	getSelectedSquadId,
	(activePot, availableGroupForPot1, availableGroupForActivePot, selectedSquad) =>
		(position: number, group: string) => {
			// This logic only for Pot 1.
			if (activePot === 1) {
				return position === 1 && availableGroupForPot1 === group;
			}

			// This logic for Pots 2-4.
			if (!selectedSquad || position === 1) {
				return false;
			}

			return availableGroupForActivePot === group;
		}
);

export const checkChangePredictionByConfederationAndGroup = createSelector(
	getAllPredictions,
	getSquads,
	(allPredictions, squads) => (confederation: Confederation, group: string, squadId: number) => {
		/*
		 * 2 teams from the same confederation cannot be drawn into the same group.  Confederations where this applies are:
		 * AFC
		 * CAF
		 * CONCACF
		 * CONEMBOL
		 * OFC
		 * A maximum of 2 UEFA teams can be drawn into the same group.
		 * */
		const countTeamAllowedInCurrentConfederation = confederation === Confederation.UEFA ? 2 : 1;

		// Get all other predictions in current group
		const predictionsInCurrentGroupWithoutSelectedPosition = filter(
			allPredictions,
			(prediction) => prediction.namegroup === group
		);

		// Converting predictions to squads
		const squadsInCurrentGroup = predictionsInCurrentGroupWithoutSelectedPosition.map(
			(prediction) => find(squads, (squad) => squad.id === prediction.squadId)
		);

		const squadsFilteredBySquadId = filter(
			squadsInCurrentGroup,
			(squad) => squad?.id !== squadId
		);

		const squadsInCurrentConfederation = filter(
			squadsFilteredBySquadId,
			(squad) => squad?.confederation === confederation
		);

		return size(squadsInCurrentConfederation) !== countTeamAllowedInCurrentConfederation;
	}
);

export const checkIsAllowedForEdit = createSelector(
	getSquadForEditId,
	checkChangePredictionByConfederationAndGroup,
	getSquads,
	getAllPredictions,
	(squadIdForEdit, checkPrediction, squads, allPrediction) => (squadId: number, pot: number) => {
		// Get squad by squad id
		const selectedSquad = find(squads, (squad) => squad.id === squadIdForEdit);

		// Get prediction by squad id
		const selectedPrediction = find(
			allPrediction,
			(prediction) => prediction.squadId === squadIdForEdit
		);

		const currentPredictionData = find(
			allPrediction,
			(prediction) => prediction.squadId === squadId
		);

		if (
			squadIdForEdit === squadId ||
			!selectedSquad ||
			!selectedPrediction ||
			!currentPredictionData ||
			LOCKED_PREDICTION.squadId === squadId
		) {
			return false;
		}

		return (
			pot === selectedSquad.pot &&
			checkPrediction(selectedSquad.confederation, currentPredictionData.namegroup, squadId)
		);
	}
);

export const checkIsPotFull = createSelector(
	getAllPredictions,
	getSquadsByPot,
	(predictions, squadsByPot) => (position: number) => {
		const squadsInCurrentPot = squadsByPot(position);
		return (
			predictions.filter((prediction) =>
				find(squadsInCurrentPot, (squad) => prediction.squadId === squad.id)
			).length === 8
		);
	}
);

export const checkIsFullPredictions = createSelector(
	getAllPredictions,
	(predictions) => predictions.length === 32
);

export const checkIsChangePredictor = createSelector(
	getPredictionsState,
	({isChangePredictor}) => isChangePredictor
);

export const isFullLockout = (state: IDrawPredictorStore) => state.predictions.isLocked;
